import React from 'react'
import './DemoLinks.scss'
import Button from '../components/Button'
import { isDebugModeAvailable, shouldUseFakeAuth, shouldUseLocalProducts } from './debugModeUtils'
import { CheckBox } from '../components/CheckBox'
import { getProductImageUrl } from '../products/getProductImageUrl'
import { BuildConfig } from '../config/BuildConfig'
import IconPencil from '../assets/icon_pencil.svg'
import ItemNoQueryParam from '../ItemNoQueryParam'
import ButtonsPopup from '../components/ButtonsPopup'
import { setLocalStorageSavedState } from '../state/LocalStorageSavedState'
import AlertPopup from '../components/AlertPopup'

const IFramePopup: React.FC<{
  url: string
  onClose: () => void
  title: string
}> = ({ url, onClose, title }) => {
  return (
    <div className="IFramePopup">
      <div className="IFramePopup-header">
        <input type="text" disabled value={`${window.location.origin}${window.location.pathname}${url}`} />
        <Button
          style={{ marginLeft: '20px' }}
          buttonStyle="debug"
          onClick={onClose}
        >
          Close
        </Button>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Button
            style={{ marginLeft: '20px' }}
            buttonStyle="debug"
            onClick={() => { return }}
          >
            New Tab
          </Button>
        </a>
      </div>
      <iframe src={url} title={title} />
    </div>
  )
}

const links: readonly {
  name: string
  label?: string
  query?: string
  path?: string
  image?: string
}[] = [{
  name: 'Default',
  query: '',
  label: 'Build your Iconic'
}, {
  name: 'Iconic 3042C-VW 2 Gang Switch',
  query: `${ItemNoQueryParam}=3042C-VW`,
  image: getProductImageUrl('3042C-VW.png')
}, {
  name: 'Pro Series P3042C-XW 2 Gang Switch Skin',
  query: `${ItemNoQueryParam}=P3042C-XW`,
  image: getProductImageUrl('P3042C-XW.png')
}, {
  name: 'Pro Series P3042VA-XW 2 Gang Switch Vertical Composite',
  query: `${ItemNoQueryParam}=P3042VA-XW`,
  image: getProductImageUrl('P3042VA-XW.png')
}, {
  name: 'Pro Series P3042HA-XW 2 Gang Switch Horizontal Composite',
  query: `${ItemNoQueryParam}=P3042HA-XW`,
  image: getProductImageUrl('P3042HA-XW.png')
}, {
  name: 'Iconic 3042C-VW 2 Gang Switch Composite - Horizontal',
  query: `${ItemNoQueryParam}=3042HA-VW`,
  image: getProductImageUrl('3042HA-VW.png')
}, {
  name: 'Iconic 3025C-VW Double Power Point',
  query: `${ItemNoQueryParam}=3025C-VW`,
  image: getProductImageUrl('3025C-VW.png')
}, {
  name: 'Styl S3041/45C-CE Double Cooker Switch',
  query: `${ItemNoQueryParam}=S3041/45C-CE`,
  image: getProductImageUrl('S3041_45C-CE.png')
}, {
  name: 'Iconic Pro P3043C-BK 3 Gang Black Switch',
  query: `${ItemNoQueryParam}=P3043C-BK`,
  image: getProductImageUrl('P3043C-BK.png')
}, {
  name: 'Iconic Pro P3025PIDC-XW Double Power Point with indicator',
  query: `${ItemNoQueryParam}=P3025PIDC-XW`,
  image: getProductImageUrl('P3025PIDC-XW.png')
}, {
  name: 'Iconic Vivid White Power Point with Dual USB Charger',
  query: `${ItemNoQueryParam}=3025USB2-VW`,
  image: getProductImageUrl('3025USB2-VW.png')
}, {
  name: 'Skin Removal Tool',
  query: `${ItemNoQueryParam}=P3000T`,
  image: getProductImageUrl('P3000T.png')
}, {
  name: 'Non-existant product',
  query: `${ItemNoQueryParam}=FAKE_PRODUCT`,
  image: getProductImageUrl('P3025PIDC-XW.png')
}, {
  name: 'This demo page',
  label: 'Open',
  path: 'demo',
}, {
  name: 'Shared List',
  label: 'Share my list',
  path: 'list/ksQCZTCCjmS54VE7XqiL',
}]

const offlineProducts = shouldUseLocalProducts()
const debug = isDebugModeAvailable()
const fakeAuth = shouldUseFakeAuth()

const DemoLinks = () => {
  const [currentLink, setCurrentLink] = React.useState<string | null>(null)
  const [shouldUseOfflineProducts, setShouldUseOfflineProducts] = React.useState(offlineProducts)
  const [shouldUseDebug, setShouldUseDebug] = React.useState(debug)
  const [shouldUseFakeAuth, setShouldUseFakeAuth] = React.useState(fakeAuth)

  const appendedQuery = `${shouldUseOfflineProducts ? '&offlineProducts' : ''}${shouldUseDebug ? '&debug' : ''}${shouldUseFakeAuth ? '&fakeAuth' : ''}`

  const [clearPopupState, setClearPopupState] = React.useState<'none' | 'confirmation' | 'success'>('none')

  return (
    <div className="DemoLinks">
      <div className="main-column">
        <h1>{BuildConfig.brand.builderTitle} Demo Links</h1>

        <fieldset>
          <legend>Tools</legend>

          <Button
            buttonStyle="redStroke"
            onClick={() => {
              setClearPopupState('confirmation')
            }}
          >
            Clear local data
          </Button>

        </fieldset>

        <fieldset>
          <legend>Options</legend>
          <CheckBox checked={shouldUseDebug} setChecked={setShouldUseDebug}>
            debug
          </CheckBox>
          <CheckBox checked={shouldUseOfflineProducts} setChecked={setShouldUseOfflineProducts}>
            offlineProducts
          </CheckBox>
          <CheckBox checked={shouldUseFakeAuth} setChecked={setShouldUseFakeAuth}>
            fakeAuth
          </CheckBox>
        </fieldset>

        {clearPopupState === 'confirmation' && (
          <ButtonsPopup
            orientation="row"
            actions={[{
              label: 'Clear',
              style: 'redStroke',
              onClick: () => {
                setLocalStorageSavedState(null)
                setClearPopupState('success')
              }
            }, {
              label: 'Cancel',
              style: 'default',
              onClick: () => {
                setClearPopupState('none')
              }
            }]}
          >
            Clear local storage and start fresh?
            <br />This will be like opening the Builder for the first time again.
          </ButtonsPopup>
        )}

        {clearPopupState === 'success' && (
          <AlertPopup
            buttonLabel="Okay"
            onClose={() => setClearPopupState('none')}
            message="Data has been cleared."
          />
        )}

        {
          links.map((link, index) => (
            <React.Fragment key={index}>
              <hr />
              <div key={link.name} className="buttonRow">
                {
                  link.image ? (
                    <img src={link.image} width="80" alt="" />
                  ) : <div />
                }
                <h3 className="title">
                  {link.name}
                </h3>
                <div
                  style={{
                    textAlign: 'right',
                  }}
                >
                  <Button
                    buttonStyle="green"
                    onClick={() => setCurrentLink(`${process.env.PUBLIC_URL}${link.path ? `/${link.path}` : ''}?${link.query ?? ''}${appendedQuery}`)}
                  >
                    {link.label ?? (
                      <>
                        <img alt="" src={IconPencil} style={{ marginRight: '10px', width: '18px' }} />
                        Build this Product
                      </>
                    )}
                  </Button>
                  <div
                    style={{
                      margin: '10px 10px 0 0',
                    }}
                  >
                    <a href={`${process.env.PUBLIC_URL}${link.path ? `/${link.path}` : ''}?${link.query ?? ''}${appendedQuery}`} target="_blank" rel="noopener noreferrer">
                      Open in new tab
                    </a>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))
        }
        {
          currentLink && (
            <IFramePopup
              url={currentLink}
              onClose={() => setCurrentLink(null)}
              title={BuildConfig.brand.builderTitle}
            />
          )
        }
      </div>
    </div>
  )
}

export default DemoLinks